<!--
  ACRY アプリからログインしたユーザー向け
  スマホ専用のアカウント設定 + 署名依頼送信ページ。
  /acryproform + /contract を1画面にまとめた構成。
-->
<template>
  <div class="aaf-page">
    <Loading v-if="this.$store.state.isLoading" />
    <AlertDialog v-if="this.$store.state.isAlertDialog" />

    <header class="aaf-header">
      <img class="aaf-header-logo" src="@/assets/acrypro/logo.svg" />
    </header>

    <main class="aaf-main">
      <h1 class="aaf-title">アカウント設定</h1>
      <p class="aaf-subtitle">本サービスをご利用いただくために<br />アカウント情報の登録と契約書への署名が必要です</p>

      <!-- 法人/個人 -->
      <section class="aaf-section">
        <div class="aaf-label-row">
          <span class="aaf-label">区分</span>
          <span class="aaf-required">必須</span>
        </div>
        <div class="aaf-radio-row">
          <label class="aaf-radio">
            <input type="radio" value="" v-model="isIndividual" />
            <span>法人</span>
          </label>
          <label class="aaf-radio">
            <input type="radio" value="true" v-model="isIndividual" />
            <span>個人事業主</span>
          </label>
        </div>
      </section>

      <!-- 責任者名 -->
      <section class="aaf-section">
        <div class="aaf-label-row">
          <span class="aaf-label">責任者名</span>
          <span class="aaf-required">必須</span>
        </div>
        <div class="aaf-grid-2">
          <input
            type="text"
            class="aaf-input"
            :class="{ 'aaf-input-error': name01error }"
            v-model="name01"
            placeholder="姓"
          />
          <input
            type="text"
            class="aaf-input"
            :class="{ 'aaf-input-error': name02error }"
            v-model="name02"
            placeholder="名"
          />
        </div>
        <div class="aaf-grid-2 mt-8">
          <input
            type="text"
            class="aaf-input"
            :class="{ 'aaf-input-error': kana01error }"
            v-model="kana01"
            placeholder="セイ"
          />
          <input
            type="text"
            class="aaf-input"
            :class="{ 'aaf-input-error': kana02error }"
            v-model="kana02"
            placeholder="メイ"
          />
        </div>
        <div class="aaf-error-area">
          <p v-if="name01error">{{ name01ErrorText }}</p>
          <p v-if="name02error">{{ name02ErrorText }}</p>
          <p v-if="kana01error">{{ kana01ErrorText }}</p>
          <p v-if="kana02error">{{ kana02ErrorText }}</p>
        </div>
      </section>

      <!-- 法人用 -->
      <template v-if="!isIndividual">
        <section class="aaf-section">
          <div class="aaf-label-row">
            <span class="aaf-label">会社名</span>
            <span class="aaf-required">必須</span>
          </div>
          <input
            type="text"
            class="aaf-input aaf-input-full"
            :class="{ 'aaf-input-error': yagouerror }"
            v-model="yagou"
            placeholder="〇〇株式会社"
          />
          <div class="aaf-error-area">
            <p v-if="yagouerror">{{ yagouErrorText }}</p>
          </div>
        </section>

        <section class="aaf-section">
          <div class="aaf-label-row">
            <span class="aaf-label">代表者名</span>
            <span class="aaf-required">必須</span>
          </div>
          <input
            type="text"
            class="aaf-input aaf-input-full"
            :class="{ 'aaf-input-error': representativeerror }"
            v-model="representative"
            placeholder="代表取締役 〇〇 〇〇"
          />
          <div class="aaf-error-area">
            <p v-if="representativeerror">代表者名を入力してください</p>
          </div>
        </section>

        <section class="aaf-section">
          <div class="aaf-label-row">
            <span class="aaf-label">部署名</span>
            <span class="aaf-required">必須</span>
          </div>
          <input
            type="text"
            class="aaf-input aaf-input-full"
            :class="{ 'aaf-input-error': busyoerror }"
            v-model="busyo"
            placeholder="〇〇部"
          />
          <div class="aaf-error-area">
            <p v-if="busyoerror">{{ busyoErrorText }}</p>
          </div>
        </section>
      </template>

      <!-- 個人用 -->
      <template v-if="isIndividual">
        <section class="aaf-section">
          <div class="aaf-label-row">
            <span class="aaf-label">屋号</span>
          </div>
          <input
            type="text"
            class="aaf-input aaf-input-full"
            :class="{ 'aaf-input-error': yagouerror }"
            v-model="yagou"
            placeholder=""
          />
          <div class="aaf-error-area">
            <p v-if="yagouerror">{{ yagouErrorText }}</p>
          </div>
        </section>
      </template>

      <!-- 住所 -->
      <section class="aaf-section">
        <div class="aaf-label-row">
          <span class="aaf-label">住所</span>
          <span class="aaf-required">必須</span>
        </div>
        <div class="aaf-zip-row">
          <span class="aaf-zip-mark">〒</span>
          <input
            type="tel"
            class="aaf-input aaf-input-zip"
            :class="{ 'aaf-input-error': ziperror }"
            v-model="zip"
            maxlength="7"
            placeholder="0000000"
          />
          <button type="button" class="aaf-zip-btn" @click="findAction">住所検索</button>
        </div>
        <select
          class="aaf-select mt-8"
          v-model="pref"
          :class="{ 'aaf-input-error': preferror }"
        >
          <option disabled value="default">都道府県を選択</option>
          <option value="1">北海道</option>
          <option value="2">青森県</option>
          <option value="3">岩手県</option>
          <option value="4">宮城県</option>
          <option value="5">秋田県</option>
          <option value="6">山形県</option>
          <option value="7">福島県</option>
          <option value="8">茨城県</option>
          <option value="9">栃木県</option>
          <option value="10">群馬県</option>
          <option value="11">埼玉県</option>
          <option value="12">千葉県</option>
          <option value="13">東京都</option>
          <option value="14">神奈川県</option>
          <option value="15">新潟県</option>
          <option value="16">富山県</option>
          <option value="17">石川県</option>
          <option value="18">福井県</option>
          <option value="19">山梨県</option>
          <option value="20">長野県</option>
          <option value="21">岐阜県</option>
          <option value="22">静岡県</option>
          <option value="23">愛知県</option>
          <option value="24">三重県</option>
          <option value="25">滋賀県</option>
          <option value="26">京都府</option>
          <option value="27">大阪府</option>
          <option value="28">兵庫県</option>
          <option value="29">奈良県</option>
          <option value="30">和歌山県</option>
          <option value="31">鳥取県</option>
          <option value="32">島根県</option>
          <option value="33">岡山県</option>
          <option value="34">広島県</option>
          <option value="35">山口県</option>
          <option value="36">徳島県</option>
          <option value="37">香川県</option>
          <option value="38">愛媛県</option>
          <option value="39">高知県</option>
          <option value="40">福岡県</option>
          <option value="41">佐賀県</option>
          <option value="42">長崎県</option>
          <option value="43">熊本県</option>
          <option value="44">大分県</option>
          <option value="45">宮崎県</option>
          <option value="46">鹿児島県</option>
          <option value="47">沖縄県</option>
        </select>
        <input
          type="text"
          class="aaf-input aaf-input-full mt-8"
          :class="{ 'aaf-input-error': address01error }"
          v-model="address01"
          placeholder="市町村番地(例：大阪市北区○-○)"
        />
        <input
          type="text"
          class="aaf-input aaf-input-full mt-8"
          v-model="address02"
          placeholder="ビル・マンション名"
        />
        <div class="aaf-error-area">
          <p v-if="address01error || ziperror || preferror">住所を入力してください</p>
        </div>
      </section>

      <!-- 電話番号 -->
      <section class="aaf-section">
        <div class="aaf-label-row">
          <span class="aaf-label">電話番号</span>
          <span class="aaf-required">必須</span>
        </div>
        <div class="aaf-tel-row">
          <input
            type="tel"
            class="aaf-input aaf-input-tel"
            :class="{ 'aaf-input-error': tel01error }"
            v-model="tel01"
          />
          <span class="aaf-tel-sep">-</span>
          <input
            type="tel"
            class="aaf-input aaf-input-tel"
            :class="{ 'aaf-input-error': tel02error }"
            v-model="tel02"
          />
          <span class="aaf-tel-sep">-</span>
          <input
            type="tel"
            class="aaf-input aaf-input-tel"
            :class="{ 'aaf-input-error': tel03error }"
            v-model="tel03"
          />
        </div>
        <div class="aaf-error-area">
          <p v-if="tel01error || tel02error || tel03error">{{ telErrorText }}</p>
        </div>
      </section>

      <!-- 署名依頼の案内 -->
      <section class="aaf-section aaf-contract">
        <h2 class="aaf-contract-title">契約書署名のご案内</h2>
        <p class="aaf-contract-text">
          登録ボタンを押すと、<br />
          <span class="aaf-contract-mail">{{ email }}</span><br />
          宛に署名依頼メールが送信されます。
        </p>
        <p class="aaf-contract-text">
          メールに記載されたURLから署名を完了させてください。
        </p>
        <ul class="aaf-contract-notes">
          <li>署名依頼メールはnoreply@gmosign.comから送信されます。</li>
          <li>メールが届かない場合は迷惑メールフォルダもご確認ください。</li>
        </ul>
      </section>

      <button type="button" class="aaf-submit" @click="sendAction">
        登録して署名依頼を送信
      </button>

      <a class="aaf-contact" @click="contact">お問い合わせ</a>
    </main>
  </div>
</template>

<script>
import { User } from "@/js/User.js";
import router from "../router";
import Loading from "@/components/modal/dialog/Loading.vue";
import AlertDialog from "@/components/acyrpro/modal/dialog/AlertDialog_Ap.vue";
import firebase from "firebase/app";
import "firebase/auth";
import { Analytics } from "@/js/Analytics.js";
import { Logger } from "@/js/utils/Logger.js";

export default {
  name: "AcryAppForm",
  components: {
    Loading,
    AlertDialog,
  },
  async created() {
    this.$store.commit("showLoading");

    const userInfo = await User.getInfo().catch(() => null);
    const fbUserData = await User.getFBAcryProUserData().catch(() => null);

    if (userInfo != null) {
      this.name01 = userInfo.name01 || "";
      this.name02 = userInfo.name02 || "";
      this.kana01 = userInfo.kana01 || "";
      this.kana02 = userInfo.kana02 || "";
      this.tel01 = userInfo.tel01 || "";
      this.tel02 = userInfo.tel02 || "";
      this.tel03 = userInfo.tel03 || "";
      this.zip = "" + (userInfo.zip01 || "") + (userInfo.zip02 || "");
      this.address01 = userInfo.addr01 || "";
      this.address02 = userInfo.addr02 || "";
      this.pref = userInfo.pref || "default";
      this.yagou = userInfo.company_name || "";
      this.busyo = userInfo.department_name || "";
    }

    if (fbUserData && fbUserData.isIndividual) {
      this.isIndividual = "true";
    }
    if (fbUserData && fbUserData.representative) {
      this.representative = fbUserData.representative;
    }

    // 既に署名依頼まで完了済の場合、モバイルからは利用不可のため案内を表示
    if (fbUserData && fbUserData.isSendRequest) {
      this.$store.commit("hideLoading");
      this.$store.commit("setAlertDialogCallback", async () => {
        await firebase.auth().signOut().catch(() => {});
        router.replace("/").catch(() => {});
      });
      this.$store.commit(
        "showMessage",
        "ご利用にはPCのブラウザからアクセスしてください。"
      );
      return;
    }

    this.$store.commit("hideLoading");
  },
  data() {
    return {
      isIndividual: "",
      name01: "",
      name02: "",
      kana01: "",
      kana02: "",
      tel01: "",
      tel02: "",
      tel03: "",
      address01: "",
      address02: "",
      zip: "",
      yagou: "",
      representative: "",
      pref: "default",
      busyo: "",
      name01error: false,
      name01ErrorText: "",
      name02error: false,
      name02ErrorText: "",
      kana01error: false,
      kana01ErrorText: "",
      kana02error: false,
      kana02ErrorText: "",
      tel01error: false,
      tel02error: false,
      tel03error: false,
      telErrorText: "",
      address01error: false,
      ziperror: false,
      yagouerror: false,
      yagouErrorText: "",
      representativeerror: false,
      busyoerror: false,
      busyoErrorText: "",
      preferror: false,
    };
  },
  computed: {
    email() {
      const user = firebase.auth().currentUser;
      return user ? user.email : "";
    },
  },
  watch: {
    isIndividual() { this.yagouerror = false; },
    name01() { this.name01error = false; },
    name02() { this.name02error = false; },
    kana01() { this.kana01error = false; },
    kana02() { this.kana02error = false; },
    tel01() { this.tel01error = false; },
    tel02() { this.tel02error = false; },
    tel03() { this.tel03error = false; },
    address01() { this.address01error = false; },
    zip() { this.ziperror = false; },
    yagou() { this.yagouerror = false; },
    representative() { this.representativeerror = false; },
    busyo() { this.busyoerror = false; },
    pref() { this.preferror = false; },
  },
  methods: {
    contact() {
      router.push("/contact");
    },
    findAction() {
      const YubinBango = require("yubinbango-core2");
      new YubinBango.Core(this.zip, (addr) => {
        this.pref = addr.region_id;
        this.address01 = `${addr.locality}${addr.street}`;
        this.address02 = "";
      });
    },
    inputCheck() {
      const spaceRegex = /\s/;
      const kanaRegex = /^[\u30A0-\u30FF\u30FC]+$/;
      const numberRegex = /^\d+$/;
      let hasError = false;

      const setError = (key, text) => {
        hasError = true;
        this[`${key}error`] = true;
        if (text != null) this[`${key}ErrorText`] = text;
      };

      if (!this.name01) setError("name01", "姓を入力してください");
      else if (spaceRegex.test(this.name01)) setError("name01", "空白を含めないでください");

      if (!this.name02) setError("name02", "名を入力してください");
      else if (spaceRegex.test(this.name02)) setError("name02", "空白を含めないでください");

      if (!this.kana01) setError("kana01", "セイを入力してください");
      else if (spaceRegex.test(this.kana01)) setError("kana01", "空白を含めないでください");
      else if (!kanaRegex.test(this.kana01)) setError("kana01", "カタカナで入力してください");

      if (!this.kana02) setError("kana02", "メイを入力してください");
      else if (spaceRegex.test(this.kana02)) setError("kana02", "空白を含めないでください");
      else if (!kanaRegex.test(this.kana02)) setError("kana02", "カタカナで入力してください");

      if (!this.tel01) { hasError = true; this.tel01error = true; this.telErrorText = "内容を記載してください"; }
      if (!this.tel02) { hasError = true; this.tel02error = true; this.telErrorText = "内容を記載してください"; }
      if (!this.tel03) { hasError = true; this.tel03error = true; this.telErrorText = "内容を記載してください"; }

      if (this.tel01 && this.tel02 && this.tel03) {
        if (!numberRegex.test(this.tel01)) { hasError = true; this.tel01error = true; this.telErrorText = "数字のみで入力してください"; }
        if (!numberRegex.test(this.tel02)) { hasError = true; this.tel02error = true; this.telErrorText = "数字のみで入力してください"; }
        if (!numberRegex.test(this.tel03)) { hasError = true; this.tel03error = true; this.telErrorText = "数字のみで入力してください"; }
      }

      if (!this.zip) { hasError = true; this.ziperror = true; }
      if (!this.pref || this.pref === "default") { hasError = true; this.preferror = true; }
      if (!this.address01) { hasError = true; this.address01error = true; }

      if (!this.isIndividual) {
        if (!this.yagou) setError("yagou", "会社名を入力してください");
        else if (spaceRegex.test(this.yagou)) setError("yagou", "空白を含めないでください");

        if (!this.representative) { hasError = true; this.representativeerror = true; }

        if (!this.busyo) setError("busyo", "部署名を入力してください");
        else if (spaceRegex.test(this.busyo)) setError("busyo", "空白を含めないでください");
      } else if (this.yagou && spaceRegex.test(this.yagou)) {
        setError("yagou", "空白を含めないでください");
      }

      return !hasError;
    },
    async sendAction() {
      if (!this.inputCheck()) return;

      // 個人の場合は部署名と代表者名はクリア
      if (this.isIndividual) {
        this.representative = "";
        this.busyo = "";
      }

      this.$store.commit("showLoading");
      const store = this.$store;
      const email = firebase.auth().currentUser.email;

      try {
        await User.authPartnerCode("XXXXXXXX");

        await User.registFBAcryProInfo({
          isIndividual: Boolean(this.isIndividual),
          representative: this.representative,
        });

        await User.registInfo({
          name01: this.name01,
          name02: this.name02,
          kana01: this.kana01,
          kana02: this.kana02,
          tel01: this.tel01,
          tel02: this.tel02,
          tel03: this.tel03,
          addr01: this.address01,
          addr02: this.address02,
          zip01: this.zip.substring(0, 3),
          zip02: this.zip.substring(3, 7),
          company_name: this.yagou,
          department_name: this.busyo,
          pref: this.pref,
          email: email,
        });

        Analytics.piiFormCompleted();

        // 署名依頼送信用のユーザー情報を取得
        const userInfo = await User.getInfo();
        userInfo.is_individual = this.isIndividual ? 1 : 0;
        userInfo.representative_name = this.representative || "";

        const response = await User.sendRequest(userInfo);
        if (!response || response["envelope_xid"] == null) {
          throw new Error("signature request failed");
        }

        await User.updateFBAcryProInfo({
          isSendRequest: true,
          envelopeXID: response["envelope_xid"],
        });

        Analytics.contractSubmitted();

        store.commit("hideLoading");
        store.commit("setAlertDialogCallback", async () => {
          await firebase.auth().signOut().catch(() => {});
          router.replace("/").catch(() => {});
        });
        store.commit(
          "showMessage",
          "署名依頼がメールで届きますのでご確認ください"
        );
      } catch (e) {
        Logger.warn("AcryAppForm sendAction failed", e);
        store.commit("hideLoading");
        store.commit("showMessage", "通信エラー");
      }
    },
  },
};
</script>

<style scoped>
.aaf-page {
  min-height: 100vh;
  background: #f6f6f9;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.aaf-header {
  width: 100%;
  max-width: 480px;
  padding: 16px 20px;
  background: #ffffff;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid #e2e2ea;
}
.aaf-header-logo {
  height: 28px;
}
.aaf-main {
  width: 100%;
  max-width: 480px;
  padding: 24px 20px 80px;
  box-sizing: border-box;
}
.aaf-title {
  font-family: 'Noto Sans JP';
  font-size: 22px;
  font-weight: 700;
  color: #8C9BC6;
  text-align: center;
  margin: 8px 0 12px;
}
.aaf-subtitle {
  font-size: 13px;
  color: #565959;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 24px;
}
.aaf-section {
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 14px;
  margin-bottom: 12px;
  text-align: left;
}
.aaf-label-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.aaf-label {
  font-weight: 700;
  font-size: 14px;
  color: #2c3e50;
}
.aaf-required {
  margin-left: 8px;
  font-size: 11px;
  color: #ffffff;
  background: #A86D69;
  border-radius: 4px;
  padding: 2px 6px;
}
.aaf-radio-row {
  display: flex;
  gap: 24px;
}
.aaf-radio {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;
}
.aaf-radio input[type="radio"] {
  width: 22px;
  height: 22px;
  accent-color: #8593bc;
}
.aaf-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.aaf-input {
  height: 44px;
  width: 100%;
  border: 1px solid #cccccc;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 16px;
  background: #ffffff;
  box-sizing: border-box;
}
.aaf-input:focus {
  border-color: #8C9BC6;
  outline: none;
}
.aaf-input-full {
  width: 100%;
}
.aaf-input-error {
  border-color: #A86D69;
  background: #fff5f5;
}
.aaf-select {
  height: 44px;
  width: 100%;
  border: 1px solid #cccccc;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 16px;
  background: #ffffff;
  box-sizing: border-box;
}
.aaf-error-area {
  color: #A86D69;
  font-size: 12px;
  margin-top: 6px;
  min-height: 0;
}
.aaf-error-area p {
  margin: 2px 0;
}
.aaf-zip-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.aaf-zip-mark {
  font-size: 16px;
  font-weight: 700;
}
.aaf-input-zip {
  flex: 1 1 auto;
  max-width: 140px;
}
.aaf-zip-btn {
  height: 44px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #8C9BC6;
  background: #ffffff;
  color: #8C9BC6;
  font-weight: 700;
  font-size: 13px;
  white-space: nowrap;
}
.aaf-tel-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.aaf-input-tel {
  width: 100%;
  text-align: center;
  flex: 1 1 0;
}
.aaf-tel-sep {
  font-size: 16px;
  color: #565959;
}
.mt-8 {
  margin-top: 8px;
}
.aaf-contract {
  background: #ffffff;
}
.aaf-contract-title {
  font-size: 16px;
  font-weight: 700;
  color: #8C9BC6;
  margin: 0 0 12px;
}
.aaf-contract-text {
  font-size: 13px;
  color: #2c3e50;
  line-height: 1.7;
  margin: 0 0 12px;
}
.aaf-contract-mail {
  font-weight: 700;
  color: #565959;
  word-break: break-all;
}
.aaf-contract-notes {
  font-size: 11px;
  color: #565959;
  line-height: 1.6;
  padding-left: 18px;
  margin: 8px 0 0;
}
.aaf-contract-notes li {
  margin-bottom: 2px;
}
.aaf-submit {
  width: 100%;
  height: 56px;
  border: none;
  border-radius: 28px;
  background: #8C9BC6;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  margin-top: 16px;
}
.aaf-submit:active {
  background: #7888b8;
}
.aaf-contact {
  display: block;
  text-align: center;
  margin-top: 20px;
  color: #565959;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
}
</style>
